import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Reveal from '../components/Reveal.jsx';
import { CATEGORY_LABELS } from '../content/site.js';

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const category = params.get('category');
  const [data, setData] = useState({ posts: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setLoading(true);
    const query = category ? `?category=${encodeURIComponent(category)}` : '';
    fetch(`/api/posts${query}`)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setData({ posts: [], categories: [] }))
      .finally(() => setLoading(false));
  }, [category]);

  // Search is client-side on purpose. The whole archive is a few dozen items
  // and already in memory, so a round trip per keystroke would be slower and
  // would add an endpoint that has to be rate-limited.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.posts;
    return data.posts.filter((p) =>
      `${p.title} ${p.excerpt || ''}`.toLowerCase().includes(q)
    );
  }, [data.posts, query]);

  const href = (p) => (p.kind === 'newsletter' ? `/newsletter/${p.slug}` : `/blog/${p.slug}`);
  const [lead, ...rest] = filtered;

  return (
    <>
      <section className="page-head">
        <div className="shell">
          <p className="eyebrow">Weekly briefing</p>
          <h1>Cyber Brief</h1>
          <p className="lede" style={{ marginTop: '1.2rem' }}>
            What actually broke this week, what it means for a business your size, and what to do
            about it. Also in <a href="/feed.xml">RSS</a>.
          </p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: '2rem' }}>
        <div className="shell">
          <div className="blog-controls">
            <label className="sr-only" htmlFor="blog-search">Search the archive</label>
            <input
              id="blog-search"
              type="search"
              className="blog-search"
              placeholder="Search the archive"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
            />
          </div>

          {data.categories.length > 1 && (
            <div className="filters">
              <button
                type="button"
                className={`filter${!category ? ' on' : ''}`}
                onClick={() => setParams({})}
              >
                All
              </button>
              {data.categories.map((c) => (
                <button
                  key={c.category}
                  type="button"
                  className={`filter${category === c.category ? ' on' : ''}`}
                  onClick={() => setParams({ category: c.category })}
                >
                  {CATEGORY_LABELS[c.category] || c.category}
                  <span className="faint mono"> {c.n}</span>
                </button>
              ))}
            </div>
          )}

          {loading && <p className="dim mono" style={{ marginTop: '2rem' }}>Loading…</p>}

          {!loading && !filtered.length && (
            <p className="dim" style={{ marginTop: '2rem' }}>
              {query
                ? <>Nothing matches “{query}”. <button type="button" className="linkish" onClick={() => setQuery('')}>Clear the search</button>.</>
                : 'Nothing published in this category yet.'}
            </p>
          )}

          {!loading && query && filtered.length > 0 && (
            <p className="faint mono" style={{ marginTop: '1.5rem', fontSize: '0.78rem' }}>
              {filtered.length} match{filtered.length === 1 ? '' : 'es'} for “{query}”
            </p>
          )}

          {lead && (
            <Reveal>
              <Link to={href(lead)} className="post-lead">
                {lead.cover_image_url && (
                  <div className="post-lead-image">
                    <img src={lead.cover_image_url} alt="" />
                  </div>
                )}
                <div>
                  <span className="tag tag-red">{CATEGORY_LABELS[lead.category] || lead.category}</span>
                  <h2 style={{ marginTop: '0.9rem' }}>{lead.title}</h2>
                  <p className="dim" style={{ marginTop: '0.9rem' }}>{lead.excerpt}</p>
                  <p className="faint mono" style={{ fontSize: '0.78rem', marginTop: '1rem' }}>
                    {lead.published_date}
                    {lead.read_time_minutes ? ` · ${lead.read_time_minutes} min read` : ''}
                  </p>
                </div>
              </Link>
            </Reveal>
          )}

          {rest.length > 0 && (
            <div className="grid grid-3" style={{ marginTop: '2.5rem' }}>
              {rest.map((p, i) => (
                <Reveal key={p.slug} delay={(i % 3) * 80}>
                  <Link to={href(p)} className="post-card">
                    {p.cover_image_url && (
                      <div className="post-card-image">
                        <img src={p.cover_image_url} alt="" loading="lazy" />
                      </div>
                    )}
                    <div className="post-card-body">
                      <span className="tag tag-red">{CATEGORY_LABELS[p.category] || p.category}</span>
                      <h3>{p.title}</h3>
                      <p className="dim">{p.excerpt}</p>
                      <p className="faint mono" style={{ fontSize: '0.75rem', margin: 0 }}>
                        {p.published_date}
                        {p.read_time_minutes ? ` · ${p.read_time_minutes} min read` : ''}
                      </p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
